import { useState, useEffect, useRef } from 'react';

const useAudio = (url) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasInitialClick, setHasInitialClick] = useState(false);
  const audioRef = useRef(null);
  const hasInteractedRef = useRef(false);

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const audio = new Audio(url);
    audio.loop = true;
    audioRef.current = audio;

    const handleLoadedData = () => {
      setLoading(false);
    };

    const handleError = (error) => {
      console.error("Audio loading error:", error);
      setLoading(false);
    };

    // iOS specific setup
    if (isIOS) {
      audio.load();
      audio.volume = 0.01;
    }

    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);

    // iOS WebKit hack
    if (isIOS) {
      document.addEventListener('touchstart', function initialTouch() {
        audio.play().then(() => {
          audio.pause();
          audio.volume = 1;
          document.removeEventListener('touchstart', initialTouch);
        }).catch(error => {
          console.error("iOS initial audio setup error:", error);
        });
      }, { once: true });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('loadeddata', handleLoadedData);
        audioRef.current.removeEventListener('error', handleError);
        audioRef.current.pause();
        setPlaying(false);
        audioRef.current = null;
      }
    };
  }, [url, isIOS]);

  const play = async () => {
    if (!audioRef.current || loading || playing) return;

    try {
      if (isIOS && !hasInteractedRef.current) {
        audioRef.current.volume = 1;
        hasInteractedRef.current = true;
      }

      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        await playPromise;
        setPlaying(true);
      }
    } catch (error) {
      console.error("Playback error:", error);
      setPlaying(false);
    }
  };

  const pause = () => {
    if (!audioRef.current || !playing) return;
    audioRef.current.pause();
    setPlaying(false);
  };

  return { 
    playing, 
    loading, 
    play, 
    pause, 
    isIOS, 
    hasInitialClick, 
    setHasInitialClick 
  };
};

export default useAudio;